

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Layout/Navbar';
import { isAuthenticated } from '../utils/auth';
import api from '../services/api';
import AppointmentForm from '../components/Admin/AppointmentForm';
import './Appointments.css';

interface Appointment {
  id: number;
  patient_id: number;
  google_event_id: string | null;
  appointment_type: string;
  date: string;
  time: string;
  status: string;
  appointment_status: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  patient_name: string;
  phone: string;
  pet_name: string;
  pet_species: string;
  pet_breed: string;
}

const Appointments: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Appointments');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchAppointments();
  }, [navigate]);

  // Fetch all appointments using /admin/all endpoint
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/all?page=1&limit=100');
      if (response.data.success) {
        const appointmentsData = response.data.data?.appointments || [];
        setAppointments(appointmentsData);
      } else {
        setError(response.data.message || 'Failed to load appointments');
      }
    } catch (err: any) {
      console.error('Error fetching appointments:', err);
      setError(err.response?.data?.error || 'Failed to load appointments data');
    } finally {
      setLoading(false);
    }
  };

  // Delete appointment using DELETE endpoint
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    try {
      // const response = await api.delete(`/admin/appointments/${id}`);
      // const response = await api.post(`/admin/cancel/${id}`);
      const response = await api.post('/admin/cancel', {
  appointment_id: id
});

      if (response.data.success) {
        setSuccessMessage('Appointment deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchAppointments();
      } else {
        alert(response.data.message || 'Failed to delete appointment');
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error deleting appointment');
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const routes: { [key: string]: string } = {
      'Dashboard': '/dashboard',
      'Calls': '/calls',
      'Conversations': '/conversations',
      'Transcriptions': '/transcriptions',
      'Timings': '/timings',
      'Doctors': '/doctors',
      'Pricing': '/pricing',
      'Appointments': '/appointments',
      'EmailConfig': '/emailconfig'
    };
    navigate(routes[tab] || '/dashboard');
  };

  const handleEdit = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingAppointment(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingAppointment(null);
  };

  // Form submit handler - just refreshes the list since form handles its own API call
  const handleFormSubmit = () => {
    fetchAppointments();
    handleFormClose();
  };

  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
  };

  const getStatusBadgeClass = (status: string | null) => {
    const displayStatus = status || 'scheduled';
    const statusMap: { [key: string]: string } = {
      'scheduled': 'status-scheduled',
      'confirmed': 'status-scheduled',
      'completed': 'status-completed',
      'cancelled': 'status-cancelled',
      'rescheduled': 'status-scheduled'
    };
    return `status-badge ${statusMap[displayStatus] || 'status-scheduled'}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDisplayStatus = (status: string | null) => {
    const displayStatus = status || 'scheduled';
    const statusMap: { [key: string]: string } = {
      'confirmed': 'Scheduled',
      'scheduled': 'Scheduled',
      'completed': 'Completed',
      'cancelled': 'Cancelled',
      'rescheduled': 'Rescheduled'
    };
    return statusMap[displayStatus] || displayStatus;
  };

  // Filter appointments locally
  const filteredAppointments = appointments.filter(app => {
    if (filterStatus === 'all') return true;
    const appStatus = app.appointment_status || app.status || 'scheduled';
    if (filterStatus === 'scheduled') {
      return appStatus === 'scheduled' || appStatus === 'confirmed';
    }
    return appStatus === filterStatus;
  });

  if (!isAuthenticated()) return null;

  if (loading) {
    return (
      <div className="appointments-page">
        <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
        <div className="page-content">
          <div className="loading-container">Loading appointments...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="appointments-page">
      <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
      <div className="page-content">
        <div className="page-header">
          <h1>📅 Booked Appointment Details</h1>
          <button className="add-button" onClick={handleAdd}>
            + New Appointment
          </button>
        </div>

        {successMessage && <div className="success-message">{successMessage}</div>}
        {error && <div className="error-message">{error}</div>}

        <div className="status-filters">
          <button
            className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            📋 All
          </button>
          <button
            className={`filter-btn ${filterStatus === 'scheduled' ? 'active' : ''}`}
            onClick={() => handleFilterChange('scheduled')}
          >
            ✅ Scheduled
          </button>
          <button
            className={`filter-btn ${filterStatus === 'completed' ? 'active' : ''}`}
            onClick={() => handleFilterChange('completed')}
          >
            ✔️ Completed
          </button>
          <button
            className={`filter-btn ${filterStatus === 'cancelled' ? 'active' : ''}`}
            onClick={() => handleFilterChange('cancelled')}
          >
            ❌ Cancelled
          </button>
        </div>

        {filteredAppointments.length === 0 && !error ? (
          <div className="no-data">
            <p>No appointments found.</p>
            <button className="add-button-secondary" onClick={handleAdd}>
              + Book Appointment
            </button>
          </div>
        ) : (
          <div className="appointments-table-container">
            <table className="appointments-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Patient Name</th>
                  <th>Phone</th>
                  <th>Pet Name</th>
                  <th>Species/Breed</th>
                  <th>Appointment Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.map((appointment) => (
                  <tr key={appointment.id}>
                    <td className="date-cell">{formatDate(appointment.date)}</td>
                    <td className="time-cell">{appointment.time}</td>
                    <td>{appointment.patient_name}</td>
                    <td className="phone-cell">{appointment.phone}</td>
                    <td>{appointment.pet_name}</td>
                    <td>{appointment.pet_species} / {appointment.pet_breed}</td>
                    <td className="appointment-type-cell">
                      <span className="appointment-type-badge">
                        {appointment.appointment_type}
                      </span>
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(appointment.appointment_status || appointment.status)}>
                        {getDisplayStatus(appointment.appointment_status || appointment.status)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn edit-action"
                          onClick={() => handleEdit(appointment)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn delete-action"
                          onClick={() => handleDelete(appointment.id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <AppointmentForm
          appointment={editingAppointment}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default Appointments;